package com.evoapps.evoshift.data.di

import com.evoapps.evoshift.data.mapper.AbsenceMapper
import com.evoapps.evoshift.data.mapper.AdjustmentMapper
import com.evoapps.evoshift.data.mapper.AvailabilityDayMapper
import com.evoapps.evoshift.data.mapper.AvailabilityMapper
import com.evoapps.evoshift.data.mapper.AvailabilitySlotMapper
import com.evoapps.evoshift.data.mapper.ConflictMetadataMapper
import com.evoapps.evoshift.data.mapper.ContractTypeMapper
import com.evoapps.evoshift.data.mapper.CostMapper
import com.evoapps.evoshift.data.mapper.CostSourceTypeMapper
import com.evoapps.evoshift.data.mapper.DailyRecordMapper
import com.evoapps.evoshift.data.mapper.DayTemplateMapper
import com.evoapps.evoshift.data.mapper.DeviceMapper
import com.evoapps.evoshift.data.mapper.EngagementMapper
import com.evoapps.evoshift.data.mapper.FinancialSummaryMapper
import com.evoapps.evoshift.data.mapper.MetadataMapper
import com.evoapps.evoshift.data.mapper.NotificationMapper
import com.evoapps.evoshift.data.mapper.OwnerMapper
import com.evoapps.evoshift.data.mapper.RatesSnapshotMapper
import com.evoapps.evoshift.data.mapper.RecurringCostRuleMapper
import com.evoapps.evoshift.data.mapper.RosterFinancialsMapper
import com.evoapps.evoshift.data.mapper.RosterMapper
import com.evoapps.evoshift.data.mapper.RosterStatusMapper
import com.evoapps.evoshift.data.mapper.SensitiveDataMapper
import com.evoapps.evoshift.data.mapper.ShiftCostsMapper
import com.evoapps.evoshift.data.mapper.ShiftFinancialsMapper
import com.evoapps.evoshift.data.mapper.ShiftMapper
import com.evoapps.evoshift.data.mapper.ShiftTemplateMapper
import com.evoapps.evoshift.data.mapper.ShiftTimeMapper
import com.evoapps.evoshift.data.mapper.SwapRequestMapper
import com.evoapps.evoshift.data.mapper.UserMapper
import com.evoapps.evoshift.data.mapper.WorkplaceFinancialsMapper
import com.evoapps.evoshift.data.mapper.WorkplaceMapper
import com.evoapps.evoshift.data.mapper.WorkplaceSettingsMapper
import com.evoapps.evoshift.data.repository.AbsenceRepositoryImpl
import com.evoapps.evoshift.data.repository.AuthRepositoryImpl
import com.evoapps.evoshift.data.repository.AvailabilityRepositoryImpl
import com.evoapps.evoshift.data.repository.DataExportRepositoryImpl
import com.evoapps.evoshift.data.repository.FinancialRepositoryImpl
import com.evoapps.evoshift.data.repository.NotificationSettingsRepositoryImpl
import com.evoapps.evoshift.data.repository.LegalCalendarRepositoryImpl
import com.evoapps.evoshift.data.repository.PlanRepositoryImpl
import com.evoapps.evoshift.data.repository.RosterRepositoryImpl
import com.evoapps.evoshift.data.repository.ShiftRepositoryImpl
import com.evoapps.evoshift.data.repository.UserRepositoryImpl
import com.evoapps.evoshift.data.repository.WorkplaceRepositoryImpl
import com.evoapps.evoshift.data.repository.OwnerRepositoryImpl
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.AuthRepository
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.domain.repository.DataExportRepository
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.NotificationSettingsRepository
import com.evoapps.evoshift.domain.repository.LegalCalendarRepository
import com.evoapps.evoshift.domain.repository.OwnerRepository
import com.evoapps.evoshift.domain.repository.PlanRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.session.LocalCache
import com.evoapps.evoshift.data.remote.FirebaseProject
import dev.gitlive.firebase.auth.FirebaseAuth
import io.ktor.client.HttpClient
import org.kodein.di.DI
import org.kodein.di.bind
import org.kodein.di.instance
import org.kodein.di.instanceOrNull
import org.kodein.di.singleton
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.map
import kotlin.time.Duration.Companion.seconds

/** Kodein tag of the tablet session's device store (SH-107). */
const val KIOSK_SESSION_STORE = "kioskSession"

val dataModule = DI.Module("dataModule") {
    // SH-177: one Firestore handle for every repository; signing out clears its offline cache.
    bind<AppFirestore>() with singleton { AppFirestore() }
    bind<LocalCache>() with singleton { instance<AppFirestore>() }
    // Callable Cloud Functions over HTTPS (functions/index.js); the engine comes from each
    // platform's Ktor dependency (OkHttp, Darwin, JS) in composeApp.
    bind<HttpClient>() with singleton { HttpClient() }
    bind<CloudFunctionsClient>() with singleton {
        val auth: FirebaseAuth = instance()
        CloudFunctionsClient(
            httpClient = instance(),
            baseUrl = FirebaseProject.functionsBaseUrl,
            idToken = { auth.currentUser?.getIdToken(false) }
        )
    }

    // Basic Mappers
    bind<MetadataMapper>() with singleton { MetadataMapper() }
    bind<AvailabilitySlotMapper>() with singleton { AvailabilitySlotMapper() }
    bind<ShiftTemplateMapper>() with singleton { ShiftTemplateMapper() }
    bind<DayTemplateMapper>() with singleton { DayTemplateMapper() }
    bind<ShiftTimeMapper>() with singleton { ShiftTimeMapper() }
    bind<ConflictMetadataMapper>() with singleton { ConflictMetadataMapper() }
    bind<SwapRequestMapper>() with singleton { SwapRequestMapper() }
    bind<RecurringCostRuleMapper>() with singleton { RecurringCostRuleMapper() }
    bind<CostSourceTypeMapper>() with singleton { CostSourceTypeMapper() }
    bind<FinancialSummaryMapper>() with singleton { FinancialSummaryMapper() }
    bind<RosterStatusMapper>() with singleton { RosterStatusMapper() }
    bind<AvailabilityDayMapper>() with singleton { AvailabilityDayMapper() }
    bind<RatesSnapshotMapper>() with singleton { RatesSnapshotMapper() }
    bind<ShiftCostsMapper>() with singleton { ShiftCostsMapper() }
    bind<SensitiveDataMapper>() with singleton { SensitiveDataMapper() }
    bind<AdjustmentMapper>() with singleton { AdjustmentMapper(instance()) }
    bind<NotificationMapper>() with singleton { NotificationMapper() }
    bind<DeviceMapper>() with singleton { DeviceMapper() }
    bind<ContractTypeMapper>() with singleton { ContractTypeMapper() }
    bind<EngagementMapper>() with singleton { EngagementMapper() }
    bind<OwnerMapper>() with singleton { OwnerMapper() }
    bind<WorkplaceFinancialsMapper>() with singleton { WorkplaceFinancialsMapper() }

    // Composite Mappers (Injecting dependencies)
    bind<UserMapper>() with singleton { UserMapper(instance()) }
    bind<WorkplaceSettingsMapper>() with singleton { WorkplaceSettingsMapper(instance()) }
    bind<WorkplaceMapper>() with singleton { WorkplaceMapper(instance()) }
    bind<RosterMapper>() with singleton { RosterMapper(instance(), instance()) }
    bind<CostMapper>() with singleton { CostMapper(instance()) }
    bind<RosterFinancialsMapper>() with singleton { RosterFinancialsMapper(instance()) }
    bind<ShiftMapper>() with singleton { ShiftMapper(instance(), instance(), instance(), instance()) }
    bind<DailyRecordMapper>() with singleton { DailyRecordMapper() }
    bind<AvailabilityMapper>() with singleton { AvailabilityMapper(instance()) }
    bind<ShiftFinancialsMapper>() with singleton { ShiftFinancialsMapper(instance(), instance()) }
    bind<AbsenceMapper>() with singleton { AbsenceMapper(instance()) }

    // Repositories
    bind<com.evoapps.evoshift.domain.repository.TipRepository>() with singleton {
        com.evoapps.evoshift.data.repository.TipRepositoryImpl(instance(), instance())
    }
    bind<com.evoapps.evoshift.domain.repository.LeaveRepository>() with singleton {
        com.evoapps.evoshift.data.repository.LeaveRepositoryImpl(instance())
    }
    bind<com.evoapps.evoshift.domain.repository.StaffRepository>() with singleton {
        com.evoapps.evoshift.data.repository.StaffRepositoryImpl(instance())
    }
    bind<UserRepository>() with singleton {
        UserRepositoryImpl(
            instance(),
            instance(),
            instance(),
            instance(),
            instance(),
            instance()
        )
    }
    bind<AuthRepository>() with singleton {
        AuthRepositoryImpl(
            instance(),
            instance(),
            instance(),
            instance(),
            instance(),
            instance(),
            // SH-240: the web app's / Play's campaign labels, sent with the sign-up.
            signupSource = instanceOrNull<com.evoapps.evoshift.domain.provider.SignupSourceProvider>()
                ?: com.evoapps.evoshift.domain.provider.SignupSourceProvider.None
        )
    }
    bind<WorkplaceRepository>() with singleton {
        WorkplaceRepositoryImpl(
            instance(),
            instance(),
            instance(),
            instance(),
            instance(),
            instance(),
            instance(),
            instance()
        )
    }
    // SH-131: the owner's shared costs (read here, saved through the callables).
    bind<com.evoapps.evoshift.domain.repository.SharedCostRepository>() with singleton {
        com.evoapps.evoshift.data.repository.SharedCostRepositoryImpl(firestore = instance(), functions = instance())
    }
    // SH-267: borrowing people from another workplace of the group (the candidates come from the server).
    bind<com.evoapps.evoshift.domain.repository.BorrowingRepository>() with singleton {
        com.evoapps.evoshift.data.repository.BorrowingRepositoryImpl(firestore = instance(), functions = instance())
    }
    // SH-267: the owner's workplace groups (read here, saved through the callable).
    // SH-270: roles and per-person access (functions/roles.js).
    bind<com.evoapps.evoshift.domain.repository.AccessRepository>() with singleton {
        com.evoapps.evoshift.data.repository.AccessRepositoryImpl(firestore = instance(), functions = instance())
    }
    bind<com.evoapps.evoshift.domain.repository.WorkplaceGroupRepository>() with singleton {
        com.evoapps.evoshift.data.repository.WorkplaceGroupRepositoryImpl(firestore = instance(), functions = instance())
    }
    // SH-269: several people's position and skills at one workplace in one batch.
    bind<com.evoapps.evoshift.domain.repository.TeamBulkRepository>() with singleton {
        com.evoapps.evoshift.data.repository.TeamBulkRepositoryImpl(instance())
    }
    // SH-269: group settings (the link read here, every change through the callables).
    bind<com.evoapps.evoshift.domain.repository.GroupSettingsRepository>() with singleton {
        com.evoapps.evoshift.data.repository.GroupSettingsRepositoryImpl(
            firestore = instance(), functions = instance(), settingsMapper = instance(), financialsMapper = instance()
        )
    }
    bind<RosterRepository>() with singleton {
        RosterRepositoryImpl(
            instance(),
            instance(),
            instance(),
        )
    }
    bind<ShiftRepository>() with singleton {
        val auth: AuthRepository = instance()
        val workplaces: WorkplaceRepository = instance()
        val rosters: RosterRepository = instance()
        // SH-255: an employee of a workplace that limits colleagues' shifts reads the month as the
        // server lets them (their own shifts directly, the rest from teamRoster).
        val privacy = com.evoapps.evoshift.domain.privacy.RosterPrivacyShiftRepository(
            inner = ShiftRepositoryImpl(
                firestore = instance(),
                shiftMapper = instance(),
                swapRequestMapper = instance(),
                conflictMetadataMapper = instance(),
                metadataMapper = instance(),
                functions = instance(),
                currentUserId = { auth.observeCurrentUser().first()?.uid }
            ),
            viewer = { auth.observeCurrentUser().first() },
            workplace = { id -> workplaces.getWorkplace(id).getOrNull() },
            rosterChanges = { id -> rosters.observeRoster(id).map { } },
            refreshEvery = 60.seconds
        )
        // SH-259: managers and owners read the managers' notes back onto the shifts.
        com.evoapps.evoshift.domain.privacy.ManagerNotesShiftRepository(privacy, viewer = { auth.observeCurrentUser().first() })
    }
    // SH-255: who is hidden in the roster for the others.
    bind<com.evoapps.evoshift.domain.privacy.RosterPrivacyRepository>() with singleton {
        com.evoapps.evoshift.data.repository.RosterPrivacyRepositoryImpl(firestore = instance(), functions = instance())
    }
    // SH-107: the clock-in tablet. The app binds the device store for the session (tag below).
    bind<com.evoapps.evoshift.domain.repository.KioskRepository>() with singleton {
        com.evoapps.evoshift.data.repository.KioskRepositoryImpl(
            auth = instance(),
            firestore = instance(),
            functions = instance(),
            shiftMapper = instance(),
            userMapper = instance(),
            sessionCache = instanceOrNull<com.evoapps.evoshift.domain.usecase.kiosk.KioskStore>(tag = KIOSK_SESSION_STORE)
                ?: com.evoapps.evoshift.domain.usecase.kiosk.InMemoryKioskStore()
        )
    }
    // SH-127: the shift's checklists on the tablet (kioskChecklist function, the person's PIN).
    bind<com.evoapps.evoshift.domain.repository.KioskChecklistRepository>() with singleton {
        com.evoapps.evoshift.data.repository.KioskChecklistRepositoryImpl(instance())
    }
    // SH-123: the till connection (functions/posImport.js).
    bind<com.evoapps.evoshift.domain.repository.PosIntegrationRepository>() with singleton {
        com.evoapps.evoshift.data.repository.PosIntegrationRepositoryImpl(firestore = instance(), functions = instance())
    }
    bind<AbsenceRepository>() with singleton {
        AbsenceRepositoryImpl(
            instance(),
            instance(),
            instance()
        )
    }
    // SH-223: remote work, work events and overtime, and the shared places to work from.
    bind<com.evoapps.evoshift.domain.repository.WorkRequestRepository>() with singleton {
        com.evoapps.evoshift.data.repository.WorkRequestRepositoryImpl(instance(), instance())
    }
    bind<AvailabilityRepository>() with singleton {
        AvailabilityRepositoryImpl(
            instance(),
            instance()
        )
    }
    bind<OwnerRepository>() with singleton {
        OwnerRepositoryImpl(
            instance(),
            instance(),
            instance()
        )
    }
    bind<PlanRepository>() with singleton {
        PlanRepositoryImpl(instance())
    }
    // SH-240: when the app last asked for a store rating (users/{uid}/settings/review).
    bind<com.evoapps.evoshift.domain.repository.ReviewPromptRepository>() with singleton {
        com.evoapps.evoshift.data.repository.ReviewPromptRepositoryImpl(instance())
    }
    // SH-239: the person's referral code for "Recommend EVoShift".
    bind<com.evoapps.evoshift.domain.repository.ReferralRepository>() with singleton {
        com.evoapps.evoshift.data.repository.ReferralRepositoryImpl(instance())
    }
    bind<NotificationSettingsRepository>() with singleton {
        NotificationSettingsRepositoryImpl(instance())
    }
    // SH-118: team announcements with read receipts, shift notes.
    bind<com.evoapps.evoshift.domain.repository.AnnouncementRepository>() with singleton {
        com.evoapps.evoshift.data.repository.AnnouncementRepositoryImpl(instance())
    }
    bind<com.evoapps.evoshift.domain.repository.ShiftNoteRepository>() with singleton {
        com.evoapps.evoshift.data.repository.ShiftNoteRepositoryImpl(instance())
    }
    // SH-125: job posts and applicants (functions/hiring.js for the public side and the CV links).
    bind<com.evoapps.evoshift.domain.repository.HiringRepository>() with singleton {
        com.evoapps.evoshift.data.repository.HiringRepositoryImpl(instance(), instance())
    }
    // SH-210: job interviews (read here, written by the scheduleInterview / cancelInterview callables).
    bind<com.evoapps.evoshift.domain.repository.InterviewRepository>() with singleton {
        com.evoapps.evoshift.data.repository.InterviewRepositoryImpl(instance(), instance())
    }
    // SH-115: the labour budget (owners and financial managers only, firestore.rules).
    bind<com.evoapps.evoshift.domain.repository.LabourBudgetRepository>() with singleton {
        com.evoapps.evoshift.data.repository.LabourBudgetRepositoryImpl(instance())
    }
    // SH-217: the roster import's code dictionary (owners and managers only, firestore.rules).
    bind<com.evoapps.evoshift.domain.repository.RosterCodeRepository>() with singleton {
        com.evoapps.evoshift.data.repository.RosterCodeRepositoryImpl(instance())
    }
    // SH-219: the owner's start path (owner only, firestore.rules).
    bind<com.evoapps.evoshift.domain.repository.StartPathRepository>() with singleton {
        com.evoapps.evoshift.data.repository.StartPathRepositoryImpl(instance())
    }
    // SH-206: who counts in the labour cost and SPMH (owners and financial managers only, firestore.rules).
    bind<com.evoapps.evoshift.domain.repository.LabourInclusionRepository>() with singleton {
        com.evoapps.evoshift.data.repository.LabourInclusionRepositoryImpl(instance())
    }
    // SH-212: the employee timeline's notes and audit trail, and the batch writing an edit back.
    bind<com.evoapps.evoshift.domain.repository.EmployeeTimelineRepository>() with singleton {
        com.evoapps.evoshift.data.repository.EmployeeTimelineRepositoryImpl(instance(), instance())
    }
    // SH-121: checklist templates and the checklists ticked on shifts.
    bind<com.evoapps.evoshift.domain.repository.ChecklistRepository>() with singleton {
        com.evoapps.evoshift.data.repository.ChecklistRepositoryImpl(instance())
    }
    // SH-226: the workplace's weather, written by functions/weather.js.
    bind<com.evoapps.evoshift.domain.repository.WeatherRepository>() with singleton {
        com.evoapps.evoshift.data.repository.WeatherRepositoryImpl(instance())
    }
    // SH-227: the events owners and managers type in (workplaces/{id}/local_events).
    bind<com.evoapps.evoshift.domain.repository.LocalEventRepository>() with singleton {
        com.evoapps.evoshift.data.repository.LocalEventRepositoryImpl(instance())
    }
    bind<LegalCalendarRepository>() with singleton {
        LegalCalendarRepositoryImpl(instance())
    }
    // SH-117: the month's working time for the accountant.
    bind<com.evoapps.evoshift.domain.repository.AccountantExportRepository>() with singleton {
        com.evoapps.evoshift.data.repository.AccountantExportRepositoryImpl(instance())
    }
    bind<DataExportRepository>() with singleton {
        DataExportRepositoryImpl(instance())
    }
    // SH-94: profile photos in Firebase Storage, over its REST API like the callable functions.
    bind<com.evoapps.evoshift.data.remote.FirebaseStorageClient>() with singleton {
        val auth: FirebaseAuth = instance()
        com.evoapps.evoshift.data.remote.FirebaseStorageClient(
            httpClient = instance(),
            baseUrl = { FirebaseProject.storageBaseUrl },
            bucket = { FirebaseProject.storageBucket },
            idToken = { auth.currentUser?.getIdToken(false) }
        )
    }
    // SH-134: the tablet's optional clock-in photos.
    bind<com.evoapps.evoshift.domain.repository.ClockPhotoRepository>() with singleton {
        com.evoapps.evoshift.data.repository.ClockPhotoRepositoryImpl(instance())
    }
    bind<com.evoapps.evoshift.domain.repository.ProfilePhotoRepository>() with singleton {
        com.evoapps.evoshift.data.repository.ProfilePhotoRepositoryImpl(instance(), instance(), instance())
    }
    // SH-122: people's documents and workplace policies (Storage files, Firestore descriptions).
    bind<com.evoapps.evoshift.domain.repository.DocumentRepository>() with singleton {
        com.evoapps.evoshift.data.repository.DocumentRepositoryImpl(instance(), instance(), instance())
    }
    // SH-126: courses and the OHS training record.
    bind<com.evoapps.evoshift.domain.repository.CourseRepository>() with singleton {
        com.evoapps.evoshift.data.repository.CourseRepositoryImpl(instance(), instance(), instance())
    }
    bind<com.evoapps.evoshift.domain.repository.ScheduleDistributionRepository>() with singleton {
        com.evoapps.evoshift.data.repository.ScheduleDistributionRepositoryImpl(instance())
    }
    // SH-132: address search through the geocodeAddress callable (OpenStreetMap Nominatim).
    bind<com.evoapps.evoshift.domain.repository.AddressSearchRepository>() with singleton {
        com.evoapps.evoshift.data.repository.AddressSearchRepositoryImpl(instance())
    }
    bind<com.evoapps.evoshift.domain.repository.AiScheduleRepository>() with singleton {
        com.evoapps.evoshift.data.repository.AiScheduleRepositoryImpl(instance())
    }
    bind<FinancialRepository>() with singleton {
        FinancialRepositoryImpl(
            instance(),
            instance(),
            instance(),
            instance(),
            instance(),
            instance()
        )
    }
}
